import React from 'react';
import Module from 'Components/Core/Module';
import useBrokerageConnections from 'Components/Banking/useBrokerageConnections';
import BrokerageLoginForm from 'Components/Banking/BrokerageLoginForm';
import BrokerageOrderForm from 'Components/Banking/BrokerageOrderForm';
import syncBrokerageNow from 'Components/Banking/syncBrokerageNow';
import './Brokerage.css';

// Rueckbau 2026-07-25: Depot+Orderbuch werden nur noch aus dem lokalen Spiegel
// (bankingBrokerageDepots/-Positions) angezeigt, OHNE jede comdirect-
// Verbindung - die wird nur bei einem expliziten "Jetzt synchronisieren"-
// Klick oder einer Order-Platzierung geoeffnet. Seit dem Umbau vom
// 2026-07-26 (Nutzervorgabe) bleibt sie danach bestehen (nicht mehr sofort
// widerrufen), bis sie explizit ueber den Logout in der Top-Navigation
// geschlossen wird, eine andere Verbindung sie ersetzt, oder sie nach
// comdirects ~10 Minuten Token-Gueltigkeit von selbst verfaellt (siehe
// BrokerageTask::syncBrokerageData()). Kein Login-Gate fuer die reine
// Ansicht noetig.
//
// Muss (wie ModuleTable es intern tut) in <Module> gewrappt werden - nur das
// registriert bei x.wam[id].show()/hide() die tatsaechliche Ein-/Ausblende-
// Logik beim Seitenwechsel. Ohne diesen Wrapper bleibt die Seite dauerhaft im
// DOM sichtbar und ueberlappt beim Navigieren mit anderen Modulen.
export default function BrokerageOverview(props) {

	const x = props.x;
	const connections = useBrokerageConnections(x);

	const [connectionId, setConnectionId] = React.useState(null);
	const [depots, setDepots] = React.useState(null);
	const [lastSyncAt, setLastSyncAt] = React.useState(null);
	const [error, setError] = React.useState('');
	// null = unsortiert (Reihenfolge der Bank). Klick auf dieselbe Spalte dreht
	// nur die Richtung um, Klick auf eine andere Spalte setzt neu auf aufsteigend.
	const [sortKey, setSortKey] = React.useState(null);
	const [sortDir, setSortDir] = React.useState(1);
	const [orderTarget, setOrderTarget] = React.useState(null);
	const [showSync, setShowSync] = React.useState(false);
	// Fortschrittsanzeige fuer das Reload-Icon (Chat vom 2026-07-26) - nur fuer
	// den schnellen Weg ueber eine bereits offene Verbindung (syncBrokerageNow);
	// braucht sie einen neuen Login, uebernimmt der Login-Dialog selbst die
	// Fortschrittsanzeige (siehe BrokerageLoginForm.js).
	const [syncing, setSyncing] = React.useState(false);

	function toggleSort(key) {
		if (sortKey === key) {
			setSortDir((current) => -current);
		} else {
			setSortKey(key);
			setSortDir(1);
		}
	}

	function sortArrow(key) {
		if (sortKey !== key) return null;
		return <i className={`bi ${sortDir === 1 ? 'bi-caret-up-fill' : 'bi-caret-down-fill'} brokerage-sort-icon`}></i>;
	}

	function positionName(position) {
		return position.name
			|| (position.instrument && (position.instrument.name || position.instrument.wkn))
			|| position.wkn || position.instrumentId || 'Position';
	}

	// Stückzahlen sind immer ganze Anteile (Aktienbruchteile gibt es nicht) -
	// die DB-Spalte quantity ist entsprechend int(11) (Chat vom 2026-07-26).
	// Math.round() bleibt als Absicherung, falls comdirect selbst doch mal
	// einen Nachkommawert liefert.
	function formatQuantity(position) {
		const value = (position.quantity && position.quantity.value) || position.quantity || '';
		return value === '' ? '' : String(Math.round(parseFloat(value)));
	}

	// Werte kommen als decimal(18,4)-String aus der DB (z.B. "13821.912") -
	// auf 2 Nachkommastellen fuer die Anzeige runden (Nutzerwunsch).
	function formatValue(value) {
		return value === undefined || value === null || value === '' ? '' : parseFloat(value).toFixed(2);
	}

	function positionQuantity(position) {
		return parseFloat((position.quantity && position.quantity.value) || position.quantity || 0);
	}

	function positionValue(position) {
		return parseFloat((position.currentValue && position.currentValue.value) || 0);
	}

	function sortedPositions(positions) {
		if (sortKey === null) return positions;
		const extract = sortKey === 'name' ? positionName : (sortKey === 'quantity' ? positionQuantity : positionValue);
		return [...positions].sort((a, b) => {
			const valueA = extract(a);
			const valueB = extract(b);
			return (typeof valueA === 'string' ? valueA.localeCompare(valueB) : valueA - valueB) * sortDir;
		});
	}

	// Erste Verbindung automatisch vorauswaehlen, statt eine unnoetige Auswahl
	// zu erzwingen - umschalten auf eine andere geht danach jederzeit ueber den
	// Verbindungs-Wechsler (switchConnection()).
	React.useEffect(() => {
		if (connectionId === null && connections && connections.length > 0) {
			setConnectionId(connections[0].connectionid);
		}
	}, [connections, connectionId]);

	function switchConnection(id) {
		if (id === connectionId) return;
		setDepots(null);
		setError('');
		setConnectionId(id);
	}

	function loadDepots() {
		x.was.axPost('Backend/webapp.php?module=Banking&task=BrokerageLocalDepotList', { connectionId }, (result) => {
			const content = result.data.content || {};
			// Debug (Chat vom 2026-07-26): zeigt die von BrokerageLocalDepotList
			// gelieferten (bereits auf unser Schema reduzierten) Depot/Positions-
			// Daten in der Konsole.
			console.log('BrokerageLocalDepotList:', content);
			if (result.data.returncode !== 1) {
				setError(content.message || 'Depot konnte nicht geladen werden.');
				return;
			}
			setError('');
			setDepots(content.depots || []);
			setLastSyncAt(content.lastSyncAt || null);
		});
	}

	React.useEffect(() => {
		if (connectionId !== null) loadDepots();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connectionId]);

	function openOrder(depot, position, side) {
		setOrderTarget({
			depotId: depot.depotId,
			depotLabel: depot.depotDisplayId,
			settlementAccountId: depot.settlementAccountId,
			side,
			instrumentId: position.instrumentId || position.wkn || '',
			instrumentLabel: positionName(position),
			instrumentIsin: position.isin || null,
			custodyCountry: position.venueCountry || null,
			// Immer mit der gehaltenen Stueckzahl vorbelegen (Nutzerwunsch), bleibt
			// natuerlich editierbar - fuer Kauf wie Verkauf gleichermassen.
			quantity: formatQuantity(position),
		});
	}

	function closeOrder() {
		setOrderTarget(null);
		loadDepots();
	}

	const connection = connections ? connections.find((c) => c.connectionid === connectionId) : null;

	let inner;
	if (connections === null) {
		inner = <p>Lade…</p>;
	} else if (connections.length === 0) {
		inner = <p>Keine comdirect-Verbindung vorhanden. Bitte zuerst unter Banking → Konten eine comdirect-Verbindung anlegen.</p>;
	} else if (connectionId === null) {
		inner = (
			<div className="brokerage-picker">
				{connections.map((c) => (
					<div key={c.connectionid} className="brokerage-picker-item" onClick={() => setConnectionId(c.connectionid)}>
						{c.bankname} – {c.accountlabel}
					</div>
				))}
			</div>
		);
	} else {
		inner = (
			<React.Fragment>
				{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
				{depots === null && <p>Lade Depot…</p>}
				{depots !== null && depots.length === 0 && <p>Noch keine Daten - bitte einmal synchronisieren.</p>}
				{(depots || []).map((depot) => {
					const totalValue = depot.currentValue && depot.currentValue.value;
					return (
						<div key={depot.depotId} className="brokerage-depot">
							<div className="brokerage-depot-header">
								<div>
									<strong>Depot {depot.depotDisplayId}</strong>
									{connection && <span className="brokerage-hint"> – Inhaber: {connection.accountlabel}</span>}
								</div>
								{totalValue && <strong>{formatValue(totalValue)} {depot.currentValue.unit || 'EUR'}</strong>}
							</div>
							{(depot.positions || []).length === 0 && <p className="brokerage-hint">Keine Positionen.</p>}
							{(depot.positions || []).length > 0 &&
								<div className="brokerage-position brokerage-position-header">
									<span className="brokerage-sortable" onClick={() => toggleSort('name')}>Wertpapier{sortArrow('name')}</span>
									<span>WKN</span>
									<span className="brokerage-sortable" onClick={() => toggleSort('quantity')}>Stückzahl{sortArrow('quantity')}</span>
									<span className="brokerage-sortable" onClick={() => toggleSort('value')}>Wert (EUR){sortArrow('value')}</span>
									<span></span>
								</div>
							}
							{sortedPositions(depot.positions || []).map((position, idx) => (
								<div
									key={position.positionId || idx}
									className="brokerage-position brokerage-position-clickable"
									onClick={() => openOrder(depot, position, 'SELL')}
								>
									<span>{positionName(position)}</span>
									<span>{position.wkn || ''}{position.venueCountry ? ` (${position.venueCountry})` : ''}</span>
									<span>{formatQuantity(position)}</span>
									<span>{formatValue(position.currentValue && position.currentValue.value)}</span>
									<span className="brokerage-position-actions">
										{position.isin &&
											<img
												src="/comdirect-favicon.ico"
												alt="comdirect"
												className="brokerage-action-icon brokerage-action-external"
												title="Bei comdirect ansehen"
												onClick={(e) => { e.stopPropagation(); window.open('https://www.comdirect.de/inf/aktien/' + encodeURIComponent(position.isin), '_blank', 'noopener,noreferrer'); }}
											/>
										}
										<i
											className="bi bi-plus-circle brokerage-action-icon brokerage-action-buy"
											title="Kaufen"
											onClick={(e) => { e.stopPropagation(); openOrder(depot, position, 'BUY'); }}
										></i>
										<i
											className="bi bi-dash-circle brokerage-action-icon brokerage-action-sell"
											title="Verkaufen"
											onClick={(e) => { e.stopPropagation(); openOrder(depot, position, 'SELL'); }}
										></i>
									</span>
								</div>
							))}
						</div>
					);
				})}
			</React.Fragment>
		);
	}

	return (
		<Module id={props.id} x={x} classes="module-brokerage-overview" title={props.title} display="d-block">
			<div className="brokerage-toolbar">
				{connections && connections.length > 1 &&
					<div className="brokerage-connection-switch">
						<label htmlFor="brokerage-connection-select">Verbindung</label>
						<select
							id="brokerage-connection-select"
							value={connectionId || ''}
							onChange={(e) => switchConnection(parseInt(e.target.value, 10))}
						>
							{connections.map((c) => (
								<option key={c.connectionid} value={c.connectionid}>{c.bankname} – {c.accountlabel}</option>
							))}
						</select>
					</div>
				}
				{connectionId !== null &&
					<i
						className={`bi bi-arrow-repeat brokerage-action-icon brokerage-toolbar-reload ${syncing ? 'brokerage-icon-spin' : ''}`}
						title="Jetzt synchronisieren"
						onClick={() => {
							if (syncing) return;
							setSyncing(true);
							setError('');
							syncBrokerageNow(x, connectionId, {
								onDone: () => { setSyncing(false); loadDepots(); },
								onNeedsLogin: () => { setSyncing(false); setShowSync(true); },
								onError: (message) => { setSyncing(false); setError(message); },
							});
						}}
					></i>
				}
				{syncing && <span className="brokerage-hint">Synchronisiere…</span>}
				{!syncing && lastSyncAt && <span className="brokerage-hint">Letzter Sync: {lastSyncAt}</span>}
			</div>
			{inner}
			{showSync &&
				<div className="brokerage-overlay">
					<BrokerageLoginForm
						x={x}
						connectionId={connectionId}
						accountLabel={connection ? connection.bankname + ' – ' + connection.accountlabel : ''}
						pinUse={true}
						pinStored={connection ? parseInt(connection.pinstored, 10) === 1 : false}
						purpose="sync"
						onLoggedIn={() => { setShowSync(false); loadDepots(); }}
						onCancel={() => setShowSync(false)}
					/>
				</div>
			}
			{orderTarget &&
				<div className="brokerage-overlay">
					<BrokerageOrderForm
						x={x}
						connectionId={connectionId}
						accountLabel={connection ? connection.bankname + ' – ' + connection.accountlabel : ''}
						pinStored={connection ? parseInt(connection.pinstored, 10) === 1 : false}
						instrumentLabel={orderTarget.instrumentLabel}
						instrumentIsin={orderTarget.instrumentIsin}
						custodyCountry={orderTarget.custodyCountry}
						depotLabel={orderTarget.depotLabel}
						initialOrder={{
							depotId: orderTarget.depotId,
							settlementAccountId: orderTarget.settlementAccountId,
							instrumentId: orderTarget.instrumentId,
							venueId: '',
							side: orderTarget.side,
							quantity: orderTarget.quantity,
							limitPrice: '',
						}}
						onCancel={closeOrder}
						onDone={closeOrder}
					/>
				</div>
			}
		</Module>
	)

};
